// production-recap.ts — production summary (book 4.9 §32): material × workshop × format aggregation view for
// project-manager control and the production director's sign-off. Display only, no pricing impact
// (this module is never called by compute()).
//
// The imposition estimate reuses the §28 usable geometry in full (same supervisor technical margins,
// same raw-material neutralisation to the gross format, rotation tested) plus one new parameter: the
// inter-piece spacing `margins.pieceGap`. It is a simple deterministic imposition, never a cutting plan:
//   rigid:    pieces/sheet = max over both orientations of ⌊usableL/(L+inter)⌋ × ⌊usableH/(H+inter)⌋;
//             sheets = ⌈qty/pieces-per-sheet⌉ per line, summed per material × format.
//   flexible: pieces/row = ⌊usable web width/(L+inter)⌋ (rotation tested, the orientation minimising
//             linear metres wins); ML = ⌈qty/pieces-per-row⌉ × (H+inter) per line, summed;
//             reels = ⌈total ML/usable length⌉ at the aggregate.
// The waste coefficient is the material's own (§27.1), never a family default; when absent the waste
// stays empty rather than a silent zero. No database read here: everything is injected or embedded.

import { MAT_FORMATS, DEFAULT_MAT_FORMAT, engineSettings } from "../../data";
import { ACTIVE_CATALOGUE, type Catalogue } from "../catalogue/catalogue-source";
import { materialNatureOf, isUpz, type MaterialNature } from "../pricing/pa-material";
import { parseFormat, neutralizedMargins, linearLengthPerPiece, piecesPerSheet, ZERO_MARGINS, type TechnicalMargins } from "../ops/material-format";
import { defaultWorkshop } from "../workshops/workshops";
import { isLargeFormatPrintRegime, isCustomPrintRegime } from "../core/regimes";
import { num, round2, round3 } from "../core/core";
import type { QuoteLine } from "../core/core";
import type { PricedLine } from "../compute/compute";

/** One piece to impose: the common grain between a quote print line and a production sub-line. */
export interface RecapPiece {
  material: string;
  /** Supplied by: "UPZ" (in-house stock) or the resolved workshop (§23). */
  suppliedBy: string;
  /** Retained material format ("L × H" mm) — null = material without format: no imposition. */
  format: string | null;
  L: number;
  H: number;
  qty: number;
  m2: number;
  /** Raw-material neutralisation (§28): geometry on the gross format, spacing included. */
  raw: boolean;
  /** Line asks for a specific web width to supply (« appro »). */
  specificSupply?: boolean;
}

/** Imposition estimate of one aggregate key (flexible or rigid — never both). */
export interface ImpositionSummary {
  nature: MaterialNature;
  /** A piece fits in no orientation of the usable format (or the format is unknown on a piece). */
  alert: boolean;
  /** Rigid: number of sheets (gross, then × (1+waste) rounded up — null when the coefficient is absent). */
  sheets?: { gross: number; withWaste: number | null };
  /** Flexible: linear metres + reels derived from the ML (with waste = ML × (1+coefficient)). */
  linearMetres?: { gross: number; withWaste: number | null };
  reels?: { gross: number; withWaste: number | null };
}

/** One aggregated line of the production summary. */
export interface ProductionRecapLine {
  /** Aggregate key: material × suppliedBy × retained format. */
  key: string;
  ref: string;
  name: string;
  suppliedBy: string;
  format: string | null;
  /** The retained format is not among the material's referenced formats -> flagged as non-standard. */
  nonStandard: boolean;
  m2: number;
  elements: number;
  nature: MaterialNature;
  /** Waste coefficient of the material (§27.1) — null = not set (empty waste, never 0). */
  wasteCoeff: number | null;
  imposition: ImpositionSummary | null;
  /** At least one aggregated line asked for a specific supply (« matière à acheter »). */
  specificSupply?: boolean;
}

/** Injected options: supervisor margins + per-material waste coefficient (§27.1) + catalogue (labels). */
export interface ProdRecapOptions {
  margins?: TechnicalMargins;
  /** material ref -> coefficient (fraction 0..1); absent/null = not set. Never a family default. */
  materialCoeff?: Readonly<Record<string, number | null | undefined>>;
  catalogue?: Catalogue;
}

// `piecesPerSheet` and `linearLengthPerPiece` live in `material-format.ts` — moved verbatim, no formula
// touched, so that `compute` can use them without depending on this display-only module.

/**
 * The production summary: aggregates pieces per material × suppliedBy × format and estimates the
 * imposition. Pure.
 * @param pieces The pieces to impose.
 * @param opts The injected margins, per-material waste coefficients and catalogue.
 * @returns One line per material × supplier × format, sorted by ref then format.
 */
export function prodRecap(pieces: readonly RecapPiece[], opts: ProdRecapOptions = {}): ProductionRecapLine[] {
  const margins = opts.margins ?? engineSettings().technicalMargins;
  const cat = opts.catalogue ?? ACTIVE_CATALOGUE;

  /** Running totals of one aggregate key. */
  interface Acc {
    firstPiece: RecapPiece;
    m2: number;
    elements: number;
    sheets: number;
    linearMm: number;
    alert: boolean;
    hasRigid: boolean;
    hasFlexible: boolean;
    specificSupply: boolean;
  }
  const byKey = new Map<string, Acc>();

  for (const p of pieces) {
    if (!p.material) continue;
    const key = `${p.material}|${p.suppliedBy}|${p.format ?? ""}`;
    const acc = byKey.get(key) ?? {
      firstPiece: p,
      m2: 0,
      elements: 0,
      sheets: 0,
      linearMm: 0,
      alert: false,
      hasRigid: false,
      hasFlexible: false,
      specificSupply: false,
    };
    if (p.specificSupply) acc.specificSupply = true;
    acc.m2 += p.m2;
    acc.elements += p.qty;

    const nature = materialNatureOf(p.material) ?? "none"; // unmapped group: skipped here, blocked by compute
    const m = p.raw ? ZERO_MARGINS : margins;
    const dims = parseFormat(p.format);
    if (dims && p.L > 0 && p.H > 0 && p.qty > 0 && nature !== "none") {
      const [width, length] = dims;
      if (nature === "flexible") {
        acc.hasFlexible = true;
        const lengthMm = linearLengthPerPiece(width - 2 * m.webMargin, length - 2 * m.reelMargin, p.L, p.H, p.qty, m.pieceGap);
        if (lengthMm == null) acc.alert = true;
        else acc.linearMm += lengthMm;
      } else {
        acc.hasRigid = true;
        const perSheet = piecesPerSheet(width - 2 * m.sheetMargin, length - 2 * m.sheetMargin, p.L, p.H, m.pieceGap);
        if (perSheet < 1) acc.alert = true;
        else acc.sheets += Math.ceil(p.qty / perSheet);
      }
    }
    byKey.set(key, acc);
  }

  const out: ProductionRecapLine[] = [];
  for (const [key, acc] of byKey) {
    const p = acc.firstPiece;
    const item = cat.materials.find((x) => x.ref === p.material);
    const nature = materialNatureOf(p.material) ?? "none";
    const coeff = opts.materialCoeff?.[p.material] ?? null;
    const referencedFormats = MAT_FORMATS[p.material] ?? [];
    const defaultFormat = DEFAULT_MAT_FORMAT[p.material];
    const nonStandard =
      !!p.format && referencedFormats.length + (defaultFormat ? 1 : 0) > 0 && !referencedFormats.includes(p.format) && p.format !== defaultFormat;

    let imposition: ImpositionSummary | null = null;
    if (acc.hasRigid) {
      imposition = {
        nature,
        alert: acc.alert,
        sheets: { gross: acc.sheets, withWaste: coeff != null ? Math.ceil(acc.sheets * (1 + coeff)) : null },
      };
    } else if (acc.hasFlexible) {
      const dims = parseFormat(p.format);
      const usableLength = dims ? dims[1] - 2 * (p.raw ? 0 : margins.reelMargin) : 0;
      const grossMetres = acc.linearMm / 1000;
      const wasteMetres = coeff != null ? grossMetres * (1 + coeff) : null;
      imposition = {
        nature,
        alert: acc.alert,
        linearMetres: { gross: round2(grossMetres), withWaste: wasteMetres != null ? round2(wasteMetres) : null },
        reels: usableLength > 0
          ? {
              gross: Math.ceil(acc.linearMm / usableLength),
              withWaste: wasteMetres != null ? Math.ceil((wasteMetres * 1000) / usableLength) : null,
            }
          : { gross: 0, withWaste: null },
      };
    } else if (acc.alert) {
      imposition = { nature, alert: true };
    }

    out.push({
      key,
      ref: p.material,
      name: item?.name ?? p.material,
      suppliedBy: p.suppliedBy,
      format: p.format,
      nonStandard,
      m2: round3(acc.m2),
      elements: acc.elements,
      nature,
      wasteCoeff: coeff ?? null,
      imposition,
      ...(acc.specificSupply ? { specificSupply: true } : {}),
    });
  }
  return out.sort((a, b) => a.ref.localeCompare(b.ref, "fr") || (a.format ?? "").localeCompare(b.format ?? "", "fr"));
}

/** Permanent notice under the block (§32) — the estimate is not a cutting plan. */
export const PROD_RECAP_NOTICE = "engineProdRecapNotice";

// Adapters: quote (print lines + calc) and production table (sub-lines) -> pieces. Same keys on both sides.

/**
 * Pieces of a quote: the priced print lines (formatRetained and m² from the calc).
 * @param lines The quote lines.
 * @param calcs The priced line of each quote line.
 * @returns The pieces to impose.
 */
export function piecesFromQuote(
  lines: readonly QuoteLine[],
  calcs: readonly (PricedLine | undefined)[],
): RecapPiece[] {
  const out: RecapPiece[] = [];
  lines.forEach((l, i) => {
    if ((!isLargeFormatPrintRegime(l) && !isCustomPrintRegime(l)) || !l.material) return;
    const c = calcs[i];
    out.push({
      material: l.material,
      suppliedBy: isUpz(l.material) ? "UPZ" : (l.workshopResp?.trim() || defaultWorkshop(l) || "—"),
      format: c?.formatRetained ?? (l.format ? String(l.format) : DEFAULT_MAT_FORMAT[l.material] ?? null),
      L: num(l.L),
      H: num(l.H),
      qty: c?.quantity ?? Math.max(1, num(l.fileQty) || 1) * Math.max(1, num(l.copiesPerFile) || 1),
      m2: c?.m2 ?? 0,
      raw: neutralizedMargins(l.print, l.cut),
      specificSupply: !!l.specificSupply,
    });
  });
  return out;
}

/**
 * Pieces of the production table: the live sub-lines (same keys — material × workshop × material format).
 * A sub-line whose status is "deleted" (the persisted status value of a deleted sub-line) is skipped.
 * @param subLines The production sub-lines (lengths and heights in mm).
 * @returns The pieces to impose.
 */
export function piecesFromSubLines(
  subLines: readonly {
    material: string | null; print: string | null; cut: string | null;
    lengthMm: number | null; heightMm: number | null; copies: number;
    materialFormat: string | null; workshop: string | null; status: string;
  }[],
): RecapPiece[] {
  const out: RecapPiece[] = [];
  for (const s of subLines) {
    if (!s.material || s.status === "deleted") continue;
    const L = s.lengthMm ?? 0;
    const H = s.heightMm ?? 0;
    const qty = Math.max(1, s.copies || 1);
    out.push({
      material: s.material,
      suppliedBy: isUpz(s.material) ? "UPZ" : (s.workshop?.trim() || "—"),
      format: s.materialFormat ?? DEFAULT_MAT_FORMAT[s.material] ?? null,
      L,
      H,
      qty,
      m2: round3((L * H * qty) / 1_000_000),
      raw: neutralizedMargins(s.print, s.cut),
    });
  }
  return out;
}
